// Helpers for the dynamic (FormBuilder) forms served by GET /api/DynamicForm/{formId}.
// The staff app stores a request's answers as formRender('userData'): the form's own field
// definitions, each input field with an extra `userData` array. buildUserData produces the same.

import { formLabelPlain } from "./formLabel";

export const DYNAMIC_FRAGMENT = "Dynamic";

const STATIC_TYPES = ["header", "paragraph"];
const OPTION_TYPES = ["select", "autocomplete", "checkbox-group", "radio-group"];
const UNSUPPORTED_TYPES = ["file", "button"];

export const isStaticField = (field) => STATIC_TYPES.includes(field?.type);

export const isInputField = (field) =>
  !!field?.type &&
  !STATIC_TYPES.includes(field.type) &&
  !UNSUPPORTED_TYPES.includes(field.type);

export const isMultiValue = (field) =>
  field?.type === "checkbox-group" ||
  (["select", "autocomplete"].includes(field?.type) && !!field.multiple);

const options = (field) => (Array.isArray(field?.values) ? field.values : []);

// The value a field starts with: its marked option(s), else the browser default (a single select
// shows its first option), else the field's own `value`.
export const defaultValue = (field) => {
  if (isMultiValue(field)) {
    return options(field)
      .filter((option) => option.selected)
      .map((option) => String(option.value ?? ""));
  }
  if (OPTION_TYPES.includes(field?.type)) {
    const marked = options(field).find((option) => option.selected);
    if (marked) return String(marked.value ?? "");
    if (field.type === "select" && options(field).length > 0) {
      return String(options(field)[0].value ?? "");
    }
    return "";
  }
  return field?.value != null ? String(field.value) : "";
};

export const buildDefaultValues = (fields) =>
  (fields || []).reduce((values, field, index) => {
    if (isInputField(field)) values[`f${index}`] = defaultValue(field);
    return values;
  }, {});

// `values` is keyed `f<index>`, as the form component registers its inputs.
export const buildUserData = (fields, values) =>
  (fields || []).map((field, index) => {
    if (!isInputField(field)) return field;
    const raw = values?.[`f${index}`];
    let userData;
    if (isMultiValue(field)) {
      userData = (Array.isArray(raw) ? raw : raw ? [raw] : []).map(String);
    } else if (field.type === "radio-group") {
      userData = raw ? [String(raw)] : [];
    } else {
      userData = [raw == null ? "" : String(raw)];
    }
    return { ...field, userData };
  });

// Reads a stored request's AdditionalData (what the staff app or buildUserData saved) back into
// display rows: [{ type, label, subtype, answers: [text] }]. Older requests keep the answer under
// `value` instead of `userData`; option fields show the option's label, not its stored value.
export const readAnswers = (additionalData) => {
  let fields;
  try {
    fields = typeof additionalData === "string" ? JSON.parse(additionalData) : additionalData;
  } catch {
    return [];
  }
  if (!Array.isArray(fields)) return [];
  return fields
    .filter((field) => field && typeof field === "object" && field.type)
    .map((field) => {
      const stored = Array.isArray(field.userData) ? field.userData : field.value;
      const raw = (Array.isArray(stored) ? stored : stored == null ? [] : [stored])
        .map((v) => (v == null ? "" : String(v)))
        .filter((v) => v !== "");
      const answers = OPTION_TYPES.includes(field.type)
        ? raw.map((v) => {
            const option = options(field).find((o) => String(o.value ?? "") === v);
            return option?.label ?? v;
          })
        : raw;
      return { type: field.type, subtype: field.subtype, className: field.className, label: formLabelPlain(field.label), answers };
    });
};
